/**
 * Clube Lidera — risco psicossocial
 *
 * Lê o COPSOQ respondido no app do colaborador, já traduzido pelo servidor para o vocabulário
 * do PGR. Nada de score nem de resposta individual chega aqui: o que vem é o agregado
 * k-anônimo, e é só isso que o Clube publica.
 */
import apiClient from './apiClient';

/** Por que a empresa não tem resultado. Nenhum destes é erro — cada um pede uma frase diferente. */
export type MotivoDaAusencia = 'integracao-desligada' | 'empresa-sem-clube' | 'sem-respostas';

export interface DimensaoDeRisco {
  /** `dimensionKey` do nosso catálogo — é por ela que a tela busca o rótulo em português. */
  chave: string;
  /** Nome da dimensão em português, pronto para exibir. */
  rotulo: string;
  dominio: string;
  /** Rotula, não calcula: o score já vem normalizado na direção de risco. */
  direcao: 'risk' | 'protective';
  ordem: number;
  total: number;
  favoravel: number;
  atencao: number;
  critico: number;
  pctFavoravel: number;
  pctAtencao: number;
  pctCritico: number;
}

export interface DominioDeRisco {
  dominio: string;
  pctCritico: number;
  nivel: 'baixo' | 'atencao' | 'alto';
}

/**
 * Risco geral do recorte — ou a recusa de dar um.
 *
 * `determinado: false` quando nenhuma dimensão ficou visível: o k-anonimato suprimiu tudo.
 * Havia gente e havia resposta, só não o bastante para publicar. A união apaga junto a
 * `periodicidadeMeses`, porque recomendar prazo de reavaliação sobre um recorte invisível é tão
 * errado quanto anunciar o nível. Espelha `RiscoGeralDoClube` do backend.
 */
export type RiscoGeralDoClube =
  | { determinado: true; nivel: 'baixo' | 'atencao' | 'alto'; periodicidadeMeses: number }
  | { determinado: false };

export interface RiscoPsicossocialDoClube {
  instrumento: string;
  riscoGeral: RiscoGeralDoClube;
  porDimensao: DimensaoDeRisco[];
  porDominio: DominioDeRisco[];
  /**
   * `responderam` e `taxa` são `null` quando o Clube suprimiu o recorte — não são zero. Dizer
   * quantas pessoas responderam num grupo pequeno já é informação sobre o grupo.
   */
  respondentes: {
    elegiveis: number;
    responderam: number | null;
    taxa: number | null;
    suprimido: boolean;
    cicloDias: number;
    periodo: string | null;
  };
  /** Dimensões medidas que não vieram por ficarem abaixo do piso de k-anonimato. */
  suprimidas: number;
  kMin: number;
  /** Dimensões novas do lado do Clube, ainda sem tradução aqui. */
  naoReconhecidas: string[];
}

export interface ResultadoDoClube {
  disponivel: boolean;
  motivo?: MotivoDaAusencia;
  risco?: RiscoPsicossocialDoClube;
}

/** Uma ação sugerida pela IA a partir do risco do Clube — proposta, nunca plano assinado. */
export interface AcaoSugeridaDoClube {
  setor: string;
  dimensao: string;
  description: string;
  actionType: string;
  priority: string;
  responsible: string;
  norma: string;
}

export interface SugestaoDoClube {
  modelo: string;
  acoes: AcaoSugeridaDoClube[];
  origem: 'clube-lidera';
  instrumento: string;
}

export interface RecorteDoRisco {
  campaignId?: string;
  department?: string;
}

class ClubeLideraService {
  private readonly baseUrl = '/psychosocial/clube';

  /**
   * Risco psicossocial da empresa, vindo do Clube.
   *
   * Ausência de dado volta como `disponivel: false` com motivo, e não como erro — a tela
   * precisa distinguir "integração desligada" de "empresa não usa o Clube" e de "ninguém
   * respondeu ainda", porque as três conversas são com pessoas diferentes.
   */
  async risco(companyId: number, recorte: RecorteDoRisco = {}): Promise<ResultadoDoClube> {
    const response = await apiClient.get<{ data: ResultadoDoClube }>(
      `${this.baseUrl}/empresas/${companyId}/risco`,
      { params: recorte }
    );

    const corpo = response.data as unknown as { data?: ResultadoDoClube };
    return corpo?.data ?? (response.data as unknown as ResultadoDoClube);
  }

  /**
   * Pede à IA um plano de ação NR-1 a partir do risco do Clube.
   *
   * É como o risco do app chega ao PGR: o documento não tem bloco psicossocial, então o caminho
   * é virar item de plano de ação, que tem quadro no PGR. As ações voltam para revisão — quem
   * assina o plano é o responsável técnico.
   */
  async sugerirPlanoDeAcao(companyId: number): Promise<SugestaoDoClube> {
    const response = await apiClient.post<{ data: SugestaoDoClube }>(
      `${this.baseUrl}/empresas/${companyId}/plano-de-acao/sugerir`
    );

    const corpo = response.data as unknown as { data?: SugestaoDoClube };
    return corpo?.data ?? (response.data as unknown as SugestaoDoClube);
  }
}

export const clubeLideraService = new ClubeLideraService();
export default clubeLideraService;
